import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Check, ChevronDown, Shield, Loader2, Headphones, BookOpen, Timer, Heart, HelpCircle, Download, ArrowRight, X, Star, Calendar, Zap, Target } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const useAnimatedCounter = (target, duration = 2000) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const t0 = performance.now();
        const step = (now) => {
          const p = Math.min((now - t0) / duration, 1);
          setCount(Math.floor((1 - Math.pow(1 - p, 3)) * target));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.3 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);
  return { count, ref };
};

const trackEvent = (event, data) => {
  if (typeof window !== 'undefined' && window.fbq) window.fbq('track', event, data);
  if (typeof window !== 'undefined' && window.gtag) window.gtag('event', event, data);
};

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCREENSHOTS = [
  { src: '/screenshots/programme.jpg', label: "Les 22 séances d'hypnose", alt: 'Séances', duration: 3000 },
  { src: '/screenshots/suivi.jpg', label: 'Journal de bord et suivi', alt: 'Suivi', duration: 4500 },
  { src: '/screenshots/timer.jpg', label: 'Timer repas', alt: 'Timer', duration: 3000 },
  { src: '/screenshots/outils.jpg', label: 'Outils pratiques', alt: 'Outils', duration: 3000 },
  { src: '/screenshots/quefairesi.jpg', label: 'Outils anti-pulsions', alt: 'Anti-pulsions', duration: 3000 },
  { src: '/screenshots/quiz.jpg', label: 'Test de compréhension', alt: 'Quiz', duration: 3000 },
];

const GOOGLE_MAPS_URL = 'https://www.google.com/maps/place/Lynda+Bry-Bonnard+Hypnose-Emdr/';

export const Landing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [demoIndex, setDemoIndex] = useState(0);
  const [reviewIndex, setReviewIndex] = useState(0);

  useEffect(() => {
    trackEvent('ViewContent', { content_name: 'Landing Page', value: 39, currency: 'EUR' });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('expired') === '1') {
      toast('Votre essai est terminé. Choisissez un accès pour continuer.', { duration: 8000 });
      window.history.replaceState({}, '', '/');
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDemoIndex(i => (i + 1) % SCREENSHOTS.length), SCREENSHOTS[demoIndex]?.duration || 3000);
    return () => clearTimeout(t);
  }, [demoIndex]);

  useEffect(() => {
    const t = setInterval(() => setReviewIndex(i => (i + 1) % 7), 6000);
    return () => clearInterval(t);
  }, []);

  const handleBuyNow = async (plan) => {
    if (!acceptedTerms) {
      toast.error('Veuillez accepter les conditions avant de continuer');
      document.getElementById('terms-section')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    setLoading(true);
    setLoadingPlan(plan);
    trackEvent('InitiateCheckout', { content_name: plan === 'onetime' ? 'Accès 39€' : 'Accompagnement 190€', value: plan === 'onetime' ? 39 : 190, currency: 'EUR' });
    if (window.gtag) window.gtag('event', 'click_accederprogramme', { event_category: 'conversion', event_label: plan === 'onetime' ? 'bouton_39euros' : 'bouton_190euros' });
    try {
      const response = await axios.post(`${API_URL}/payment/create-checkout`, { origin_url: window.location.origin, plan });
      if (response.data.checkout_url) window.location.href = response.data.checkout_url;
    } catch { toast.error('Erreur lors de la création du paiement'); setLoading(false); setLoadingPlan(null); }
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const rq = '’';

  const faqItems = [
    { q: 'Est-ce un régime ?', a: "Non. Déclic RePas ne repose pas sur des menus imposés, des calories à compter ou des interdits. L’objectif est de modifier progressivement vos réflexes alimentaires.", icon: X },
    { q: "Est-ce seulement de l’hypnose ?", a: "Non. Les séances d’hypnose font partie de la méthode, mais elles sont associées à des outils pratiques à utiliser au quotidien.", icon: Headphones },
    { q: "Quelle différence entre 39 € et 190 € ?", a: "L’offre à 39 € donne accès à la méthode complète. L’offre à 190 € ajoute 2 séances individuelles avec Lynda Bry-Bonnard, en visio.", icon: HelpCircle },
    { q: "Les audios sont-ils téléchargeables ?", a: "Oui. Vous pouvez les écouter hors connexion.", icon: Download },
    { q: "Vais-je avoir accès immédiatement ?", a: "Oui. Après paiement, l’accès est immédiat.", icon: Zap },
    { q: "Dois-je écouter les 22 séances dans l’ordre ?", a: "Non. La méthode est un ensemble d’outils. Vous utilisez ce dont vous avez besoin, quand vous en avez besoin. Les séances d’hypnose complètent les outils pratiques.", icon: Calendar },
    { q: "Est-ce adapté en cas de trouble alimentaire ?", a: "Ce programme ne remplace pas un suivi médical spécialisé. Demandez l’avis de votre professionnel de santé.", icon: Shield },
  ];

  return (
    <div className="min-h-screen bg-white pb-16 sm:pb-0" data-testid="landing-page">

      {/* ====== 1. HERO ====== */}
      <div className="bg-sage-50 px-6 pt-6 pb-14">
        <div className="max-w-4xl mx-auto">
          {/* Top bar */}
          <div className="flex items-center justify-between mb-10">
            <p className="text-declic font-semibold text-lg">Déclic RePas</p>
            <div className="flex items-center gap-2">
              <Button onClick={() => navigate('/login')} variant="outline" className="border-sage-300 text-sage-700 hover:bg-sage-100 h-9 rounded-lg text-xs font-medium px-4" data-testid="topbar-login-btn">Connexion</Button>
              <Button onClick={() => navigate('/signup')} variant="outline" className="border-declic/30 text-declic hover:bg-declic/5 h-9 rounded-lg text-xs font-medium px-4" data-testid="topbar-signup-btn">J{rq}ai un code</Button>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-center lg:gap-12">
            {/* Texte à gauche */}
            <div className="flex-1">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mb-6 text-sage-700" data-testid="hero-title">
            Vous savez quoi faire pour perdre du poids.<br/>
            <span className="text-declic">Mais quand l{rq}envie de manger arrive, vous craquez quand même ?</span>
          </h1>

          <p className="text-sage-600 text-base sm:text-lg leading-relaxed mb-6 max-w-xl">
            Vous ne mangez pas toujours parce que vous avez faim. Stress, fatigue, contrariété, ennui, besoin de réconfort… parfois, l{rq}envie de manger apparaît alors que votre corps n{rq}a pas réellement besoin de nourriture.
          </p>

          <p className="text-sage-700 text-base sm:text-lg leading-relaxed mb-8 max-w-xl font-medium">
            Découvrez Déclic RePas : une méthode qui réunit des outils pratiques et concrets à utiliser au moment où vous en avez besoin.
          </p>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-4">
            <Button onClick={() => scrollTo('pricing-section')} className="bg-declic hover:bg-declic/90 text-white h-14 rounded-xl text-base font-semibold px-8" data-testid="hero-cta-main">
              Découvrir Déclic RePas — 39 €
            </Button>
            <div>
              <p className="text-sage-700 text-xl font-bold">39 € <span className="text-sage-400 text-sm font-normal">— paiement unique</span></p>
            </div>
          </div>

          <button onClick={() => scrollTo('extraits')} className="flex items-center gap-2 text-sage-500 hover:text-sage-700 text-sm font-medium mt-4 transition-colors" data-testid="hero-listen-cta">
            <Headphones className="w-4 h-4" />
            Écoutez un extrait gratuitement
              </button>
            </div>

            {/* Image à droite (desktop) */}
            <div className="hidden lg:block flex-shrink-0 w-80 xl:w-96">
              <img
                src="/hero-outils.png"
                alt="Outils Déclic RePas"
                className="rounded-2xl shadow-lg object-cover w-full"
                loading="eager"
              />
            </div>
          </div>

          {/* Image mobile (en dessous) */}
          <div className="lg:hidden mt-8">
            <img
              src="/hero-outils.png"
              alt="Outils Déclic RePas"
              className="rounded-2xl shadow-lg object-cover w-full max-w-sm mx-auto"
              loading="eager"
            />
          </div>
        </div>
      </div>

      {/* Phrase résultat */}
      <div className="bg-white px-6 py-6">
        <p className="text-center text-sage-700 text-lg sm:text-xl font-semibold max-w-lg mx-auto">
          Arrêtez enfin de manger vos émotions.
        </p>
      </div>

      {/* ====== 2. QUE FAIRE QUAND L'ENVIE ARRIVE ? ====== */}
      <div className="px-6 py-14 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-sage-700 text-center mb-3">
            Concrètement, que faire quand l{rq}envie arrive ?
          </h2>
          <p className="text-sage-500 text-sm text-center mb-10 max-w-lg mx-auto">
            La méthode vous donne une réponse à chaque situation.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { img: '/visuals/envie-manger.jpg', title: 'Envie de manger ?', desc: 'Faim ou envie ? Apprenez à faire la différence.', cta: 'Outil pratique' },
              { img: '/visuals/chocolat.jpg', title: 'Sur le point de craquer ?', desc: 'Un outil anti-pulsions pour passer le cap.', cta: 'Outil pratique' },
              { img: '/visuals/sablier.jpg', title: 'Vous mangez trop vite ?', desc: 'Un timer pour ralentir et mieux savourer.', cta: 'Outil pratique' },
              { img: '/visuals/casque.jpg', title: 'Automatismes ?', desc: 'Des séances d’hypnose au calme pour travailler en profondeur.', cta: 'Mode concentré' },
              { img: '/visuals/marche.jpg', title: 'En mouvement ?', desc: 'Écoutez vos séances en marchant, pour un double ancrage.', cta: 'Mode actif' },
              { img: '/visuals/suivi.jpg', title: 'Suivez votre évolution', desc: 'Un journal pour repérer vos progrès et rester motivée.', cta: 'Suivi personnalisé' },
            ].map((card, i) => (
              <div key={i} className="bg-sage-50 rounded-2xl overflow-hidden border border-sage-100 flex flex-col">
                <img src={card.img} alt={card.title} className="w-full aspect-square object-cover" loading="lazy" />
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className="text-sage-700 font-bold text-sm mb-1">{card.title}</h3>
                  <p className="text-sage-500 text-xs leading-relaxed mb-3 flex-1">{card.desc}</p>
                  <button onClick={() => scrollTo('pricing-section')} className="text-sage-600 text-xs font-medium flex items-center gap-1 hover:text-declic transition-colors">
                    {card.cta} <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ====== 3. UNE JOURNÉE AVEC DÉCLIC RE.PAS ====== */}
      <div className="px-6 py-14 bg-sage-50">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-semibold text-sage-700 text-center mb-10">
            Une journée avec Déclic RePas
          </h2>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-sage-200 hidden sm:block" />

            <div className="space-y-6">
              {[
                { time: "Avant un repas", text: "Vous vérifiez si vous avez faim ou simplement envie.", icon: HelpCircle },
                { time: "Pendant le repas", text: "Le timer vous aide à ralentir.", icon: Timer },
                { time: "Une envie ou une pulsion", text: "Vous ouvrez l’outil adapté.", icon: Shield },
                { time: "Le soir, si besoin", text: "Vous écoutez une séance d’hypnose.", icon: Headphones },
                { time: "Quand vous voulez", text: "Vous notez dans le journal de bord.", icon: BookOpen },
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-4 sm:pl-2">
                  <div className="flex-shrink-0 w-10 h-10 bg-white rounded-full border-2 border-declic flex items-center justify-center z-10">
                    <step.icon className="w-5 h-5 text-declic" />
                  </div>
                  <div className="pt-1">
                    <p className="text-sage-700 font-semibold text-base">{step.time}</p>
                    <p className="text-sage-500 text-sm">{step.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-sage-600 text-sm sm:text-base text-center mt-10 max-w-md mx-auto leading-relaxed">
            Vous n{rq}avez pas besoin de suivre un parcours rigide. Vous utilisez ce dont vous avez besoin, quand vous en avez besoin.
          </p>
        </div>
      </div>

      {/* ====== 4. CE QUE VOUS AVEZ DANS LA MÉTHODE ====== */}
      <div id="method-section" className="px-6 py-14 bg-white">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-semibold text-sage-700 text-center mb-2">
            Ce que vous avez dans la méthode
          </h2>
          <p className="text-sage-500 text-sm text-center mb-8">Tout est inclus. Rien à ajouter.</p>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { icon: HelpCircle, title: 'Outil faim ou envie', desc: "Différenciez la vraie faim de l’envie émotionnelle." },
              { icon: Shield, title: 'Outils anti-pulsions', desc: "Agissez au moment où l’envie monte." },
              { icon: Timer, title: 'Timer repas', desc: "Ralentissez et écoutez la satiété." },
              { icon: Headphones, title: "22 séances d’hypnose", desc: "Sur 11 thèmes liés à la perte de poids." },
              { icon: BookOpen, title: 'Journal de bord', desc: "Suivez vos progrès sans obsession." },
              { icon: Target, title: '10 leviers pratiques', desc: "Pour agir sur vos habitudes alimentaires." },
              { icon: Download, title: "2 modes d’écoute", desc: "Concentré (au calme) ou actif (en mouvement)." },
              { icon: Zap, title: 'Test de compréhension', desc: "Vérifiez votre progression." },
              { icon: Heart, title: 'Accès à vie', desc: "Audios téléchargeables. Utilisez aussi longtemps que nécessaire." },
            ].map((f, i) => (
              <div key={i} className="bg-sage-50 rounded-xl p-4 border border-sage-100">
                <f.icon className="w-6 h-6 text-declic mb-2" />
                <h3 className="font-semibold text-sage-700 text-sm mb-1">{f.title}</h3>
                <p className="text-xs text-sage-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Button onClick={() => scrollTo('pricing-section')} className="bg-declic hover:bg-declic/90 text-white h-12 rounded-xl text-base font-semibold px-8">
              Découvrir Déclic RePas — 39 €
            </Button>
          </div>
        </div>
      </div>

      {/* ====== 5. DÉMO ANIMÉE (SCREENSHOTS) ====== */}
      <div className="px-6 py-14 bg-sage-500 text-white">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-semibold text-center mb-8">
            La méthode en images
          </h2>

          <div className="flex justify-center mb-6">
            <div className="relative w-[220px] sm:w-[260px]">
              <div className="bg-gray-900 rounded-[2rem] p-2 shadow-2xl">
                <div className="bg-white rounded-[1.5rem] overflow-hidden relative" style={{ aspectRatio: '390/844' }}>
                  {SCREENSHOTS.map((s, i) => (
                    <img key={i} src={s.src} alt={s.alt} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === demoIndex ? 'opacity-100' : 'opacity-0'}`} />
                  ))}
                </div>
              </div>
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-gray-900 rounded-b-xl" />
            </div>
          </div>

          <p className="text-white/90 text-sm sm:text-base text-center font-medium mb-2">{SCREENSHOTS[demoIndex]?.label}</p>
          <div className="flex justify-center gap-2 mb-8">
            {SCREENSHOTS.map((_, i) => (
              <button key={i} onClick={() => setDemoIndex(i)} className={`w-2 h-2 rounded-full transition-all ${i === demoIndex ? 'bg-declic w-5' : 'bg-white/30'}`} />
            ))}
          </div>

          <div className="text-center">
            <Button onClick={() => scrollTo('pricing-section')} className="bg-declic hover:bg-declic/90 text-white h-12 rounded-xl text-base font-semibold px-8">
              Découvrir Déclic RePas — 39 €
            </Button>
          </div>
        </div>
      </div>

      {/* ====== 6. VIDÉO ====== */}
      <div className="px-6 py-14 bg-white">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-semibold text-sage-700 text-center mb-3">
            Lynda vous explique la méthode
          </h2>
          <p className="text-sage-500 text-sm text-center mb-6">2 minutes pour comprendre pourquoi les régimes ne fonctionnent pas</p>
          <div className="relative rounded-2xl overflow-hidden shadow-lg" style={{ aspectRatio: '16/9' }}>
            <iframe src="https://www.youtube.com/embed/omiVHjv9KKM?rel=0" title="Déclic RePas" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" loading="lazy" />
          </div>
        </div>
      </div>

      {/* ====== 7. LA PROFESSIONNELLE DERRIÈRE DÉCLIC RE.PAS ====== */}
      <div className="px-6 py-14 bg-sage-50">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-semibold text-sage-700 text-center mb-8">
            La professionnelle derrière Déclic RePas
          </h2>

          <div className="flex flex-col sm:flex-row gap-6 items-start mb-8">
            <div className="flex-shrink-0 mx-auto sm:mx-0">
              <img src="https://lyndabrybonnard.fr/wp-content/uploads/2025/03/Lynda-bry-bonnard-hypnose-emdr-1.png" alt="Lynda Bry-Bonnard" className="w-36 h-36 sm:w-44 sm:h-44 rounded-2xl object-cover object-top shadow-md" loading="lazy" />
            </div>
            <div className="flex-1">
              <p className="text-sage-700 text-base leading-relaxed mb-3">
                <strong>Lynda Bry-Bonnard</strong>, hypnothérapeute et praticienne EMDR, spécialisée dans les accompagnements liés au poids et aux comportements alimentaires.
              </p>
              <p className="text-sage-600 text-sm leading-relaxed mb-3">
                Déclic RePas est né de l{rq}accompagnement de personnes qui ne manquent pas d{rq}information sur l{rq}alimentation, mais qui restent bloquées dans des réflexes : manger trop vite, manger sans faim, grignoter, culpabiliser, recommencer.
              </p>
              <a href="https://lyndabrybonnard.fr/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sage-500 hover:text-sage-700 text-sm font-medium">
                En savoir plus <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Lynda 2002 — argument clé */}
          <div className="bg-white rounded-2xl p-6 border-2 border-declic/20 shadow-md mb-8" data-testid="lynda-2002-card">
            <div className="flex items-center gap-5">
              <img src="/lynda-2002.png" alt="Lynda en 2002" className="w-24 h-24 rounded-xl object-cover flex-shrink-0 shadow-sm" loading="lazy" />
              <div>
                <p className="text-sage-700 text-lg font-bold mb-2">Oui, j{rq}étais jeune, brune... et je faisais 88 kilos à cette époque 😄</p>
                <p className="text-sage-600 text-sm leading-relaxed">Déclic RePas, je l{rq}ai construit en le testant d{rq}abord sur moi-même, avant de le proposer à mes clientes.</p>
              </div>
            </div>
          </div>

          {/* Avis Google — clairement sur l'accompagnement pro */}
          <div className="bg-white rounded-2xl p-6 border border-sage-100">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sage-700 font-semibold text-sm">Avis sur l{rq}accompagnement de Lynda</p>
              <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 group">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}
                </div>
                <span className="text-sage-700 font-bold text-xs">5,0/5</span>
                <span className="text-sage-400 text-xs group-hover:text-sage-600">(187 avis)</span>
              </a>
            </div>

            <div className="relative overflow-hidden">
              <div className="flex transition-transform duration-500" style={{ transform: `translateX(-${reviewIndex * 100}%)` }}>
                {[
                  { name: 'Stéphanie S.', text: "Grâce à son accompagnement en hypnose, j’ai pu avancer dans ma perte de poids sereinement. Je recommande vivement !", initial: 'S', color: 'bg-declic' },
                  { name: 'Cléo L.', text: "Je vous recommande à 10000 % ! Aide à se séparer de ses habitudes tout en douceur et sans frustration.", initial: 'C', color: 'bg-sage-400' },
                  { name: 'Deru D.', text: "Troisième séance pour 3 sujets différents. À chaque fois je suis reprogrammée et reboostée. Ça fonctionne !", initial: 'D', color: 'bg-amber-600' },
                  { name: 'Sandrine B.', text: "Lynda est une thérapeute qui met à l’aise, qui explique clairement sa démarche. Je recommande vivement.", initial: 'S', color: 'bg-sage-500' },
                  { name: 'Delphine P.', text: "Lynda a su m’aider à enlever un poids que j’avais depuis des années. Je me sens mieux aujourd’hui.", initial: 'D', color: 'bg-purple-500' },
                  { name: 'Aurore A.', text: "Exceptionnel. Merci.", initial: 'A', color: 'bg-orange-500' },
                  { name: 'Louise', text: "Je viens de passer les 8 kg en moins, c’est génial. Merci beaucoup.", initial: 'L', color: 'bg-declic' },
                ].map((r, i) => (
                  <div key={i} className="w-full flex-shrink-0 px-1">
                    <p className="text-sage-700 text-sm leading-relaxed mb-3">{r.text}</p>
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 ${r.color} text-white rounded-full flex items-center justify-center text-xs font-semibold`}>{r.initial}</div>
                      <span className="text-sage-500 text-xs">{r.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center gap-1.5 mt-4">
              {[0,1,2,3,4,5,6].map(i => (
                <button key={i} onClick={() => setReviewIndex(i)} className={`w-1.5 h-1.5 rounded-full transition-all ${i === reviewIndex ? 'bg-declic w-4' : 'bg-sage-300'}`} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ====== 8. PRIX ====== */}
      <div id="pricing-section" className="px-6 py-14 bg-white">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-2xl p-6 border-2 border-declic shadow-md" data-testid="plan-onetime">
            <h2 className="font-semibold text-sage-700 text-xl mb-1">Déclic RePas</h2>
            <p className="text-4xl font-bold text-sage-800 mb-1">39 €</p>
            <p className="text-sage-500 text-base mb-6">Paiement unique. Pas d{rq}abonnement.</p>

            <ul className="space-y-2.5 mb-6">
              {[
                'Méthode complète',
                "22 séances d’hypnose",
                'Outils anti-pulsions',
                'Timer repas',
                'Outil faim ou envie',
                'Journal de bord',
                'Audios téléchargeables',
                'Accès à vie',
                'Mises à jour incluses',
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-declic flex-shrink-0" />
                  <span className="text-sage-700 text-sm">{item}</span>
                </li>
              ))}
            </ul>

            <Button onClick={() => handleBuyNow('onetime')} disabled={loading} className="w-full bg-declic hover:bg-declic/90 text-white h-13 rounded-xl text-base font-semibold" data-testid="buy-onetime">
              {loadingPlan === 'onetime' ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Découvrir Déclic RePas — 39 €'}
            </Button>
            <p className="text-sage-400 text-xs text-center mt-2">Accès immédiat. Paiement sécurisé. 3x sans frais avec Klarna.</p>
          </div>

          <div id="terms-section" className="mt-4">
            <label className="flex items-start gap-3 p-4 bg-sage-50 rounded-xl cursor-pointer text-left border border-sage-200">
              <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="mt-1 w-4 h-4 rounded border-sage-300 text-declic focus:ring-declic" />
              <span className="text-xs text-sage-500 leading-relaxed">
                J{rq}accepte que l{rq}exécution du programme commence dès l{rq}achat et je renonce à mon droit de rétractation (art. L221-28).
              </span>
            </label>
          </div>

          <div className="flex items-center justify-center gap-2 text-sage-400 text-xs mt-4">
            <Shield className="w-3.5 h-3.5" />
            <span>Paiement sécurisé par Stripe</span>
          </div>

          <div className="bg-sage-50 rounded-xl p-4 border border-sage-200 mt-4 text-center">
            <p className="text-sage-700 text-sm font-medium mb-1">14 jours pour tester la méthode</p>
            <p className="text-sage-500 text-xs leading-relaxed">
              Vous l’essayez pendant 14 jours. Si elle ne vous convient pas, contactez-moi et je vous rembourse. Sans condition.
            </p>
          </div>

          <p className="text-sage-400 text-xs text-center mt-6">
            Votre méthode est disponible à tout moment sur votre téléphone.
          </p>
        </div>
      </div>

      {/* ====== 9. ACCOMPAGNEMENT ====== */}
      <div className="px-6 py-12 bg-sage-50">
        <div className="max-w-md mx-auto text-center">
          <h2 className="text-xl sm:text-2xl font-semibold text-sage-700 mb-3">
            Vous préférez être accompagnée ?
          </h2>
          <p className="text-sage-600 text-sm leading-relaxed mb-6">
            Déclic RePas fonctionne en autonomie complète. Si vous souhaitez un accompagnement personnalisé, 2 séances en visio sont disponibles en option.
          </p>

          <div className="bg-white rounded-2xl p-6 border border-sage-200 text-left" data-testid="plan-accompagnement">
            <h3 className="font-semibold text-sage-700 text-lg mb-1">Déclic RePas + Coaching</h3>
            <p className="text-3xl font-bold text-sage-800 mb-1">190 €</p>
            <p className="text-sage-500 text-sm mb-1">Méthode + 2 séances individuelles</p>
            <p className="text-declic text-xs font-medium mb-4">au lieu de 199 € (39 € + 2 × 80 €)</p>

            <ul className="space-y-2 mb-5">
              {["Tout l’accès complet à 39 €", '2 séances individuelles en visio', 'Aide au démarrage', 'Adaptation aux blocages personnels'].map((item, i) => (
                <li key={i} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-sage-500 flex-shrink-0" />
                  <span className="text-sage-700 text-sm">{item}</span>
                </li>
              ))}
            </ul>

            <Button onClick={() => handleBuyNow('accompagnement')} disabled={loading} variant="outline" className="w-full border-sage-300 text-sage-700 hover:bg-sage-50 h-12 rounded-xl text-base font-semibold" data-testid="buy-accompagnement">
              {loadingPlan === 'accompagnement' ? <Loader2 className="w-5 h-5 animate-spin" /> : "Choisir l’accompagnement"}
            </Button>
            <p className="text-sage-400 text-xs text-center mt-2">3x sans frais avec Klarna</p>
          </div>
        </div>
      </div>

      {/* ====== EXTRAITS GRATUITS ====== */}
      <div className="px-6 py-12 bg-white" id="extraits">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-semibold text-sage-700 text-center mb-3">
            Écoutez un extrait gratuit
          </h2>
          <p className="text-sage-500 text-sm text-center mb-8 max-w-lg mx-auto">
            Découvrez 7 minutes de deux séances d’hypnose du programme. Casque ou écouteurs recommandés.
          </p>

          <div className="space-y-4">
            {/* Extrait 1 — Pulsions mode zen */}
            <div className="bg-sage-50 rounded-xl p-5 border border-sage-100">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 bg-sage-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Headphones className="w-5 h-5 text-sage-500" />
                </div>
                <div>
                  <p className="text-sage-700 font-semibold text-base">Pulsions — mode zen</p>
                  <p className="text-sage-400 text-xs">À écouter assis(e), au calme, Casque ou écouteurs.</p>
                </div>
              </div>
              <audio controls preload="none" className="w-full h-10" data-testid="audio-extrait-1">
                <source src="/extraits/extrait-pulsions-zen.mp3" type="audio/mpeg" />
              </audio>
              <p className="text-sage-400 text-xs mt-2">Extrait de 7 min — Séance complète disponible dans la méthode</p>
            </div>

            {/* Extrait 2 — Motivation mode mouvement */}
            <div className="bg-sage-50 rounded-xl p-5 border border-sage-100">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 bg-declic/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Headphones className="w-5 h-5 text-declic" />
                </div>
                <div>
                  <p className="text-sage-700 font-semibold text-base">Motivation — mode mouvement</p>
                  <p className="text-sage-400 text-xs">À écouter en marchant, en activité ou en mouvement. Casque ou écouteurs.</p>
                </div>
              </div>
              <audio controls preload="none" className="w-full h-10" data-testid="audio-extrait-2">
                <source src="/extraits/extrait-motivation-mouvement.mp3" type="audio/mpeg" />
              </audio>
              <p className="text-sage-400 text-xs mt-2">Extrait de 7 min — Séance complète disponible dans la méthode</p>
            </div>
          </div>
        </div>
      </div>

      {/* ====== 10. FAQ ====== */}
      <div className="px-6 py-14 bg-white">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-semibold text-sage-700 text-center mb-8">Questions fréquentes</h2>
          <div className="space-y-3">
            {faqItems.map((item, i) => (
              <div key={i} className="border border-sage-200 rounded-xl overflow-hidden bg-white">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center gap-3 p-4 text-left">
                  <item.icon className="w-5 h-5 text-sage-400 flex-shrink-0" />
                  <span className="text-base font-medium text-sage-700 flex-1 pr-2">{item.q}</span>
                  <ChevronDown className={`w-5 h-5 text-sage-400 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-4 pb-4 pl-12">
                    <p className="text-sage-600 text-sm leading-relaxed">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Note médicale */}
      <div className="px-6 py-4 bg-white">
        <p className="text-sage-400 text-xs text-center leading-relaxed max-w-lg mx-auto">
          En cas de maladie, grossesse, diabète, trouble du comportement alimentaire ou situation médicale particulière, demandez l{rq}avis de votre professionnel de santé.
        </p>
      </div>

      {/* Contact */}
      <div className="px-6 py-8 bg-sage-50">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-sage-700 font-medium text-base mb-1">Une question ?</p>
          <p className="text-sage-500 text-sm mb-3">Lynda Bry-Bonnard — Hypnothérapeute</p>
          <a href="mailto:brylynda@gmail.com" className="text-declic hover:underline text-sm font-medium">brylynda@gmail.com</a>
          <span className="text-sage-300 mx-2">|</span>
          <a href="tel:0664260050" className="text-declic hover:underline text-sm font-medium">06 64 26 00 50</a>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-sage-500 text-white px-6 py-6">
        <div className="max-w-2xl mx-auto">
          <p className="text-center text-white/80 text-sm">© 2026 Déclic RePas — Méthode créée par Lynda Bry-Bonnard</p>
          <div className="flex flex-wrap justify-center gap-3 mt-3 text-xs text-white/50">
            <a href="/mentions-legales" className="hover:text-white underline">Mentions légales</a>
            <a href="/cgv" className="hover:text-white underline">CGV</a>
            <a href="/cgu" className="hover:text-white underline">CGU</a>
            <a href="/confidentialite" className="hover:text-white underline">Confidentialité</a>
          </div>
        </div>
      </div>

      {/* CTA flottant mobile */}
      <div className="fixed bottom-0 left-0 right-0 z-50 sm:hidden bg-white/95 backdrop-blur-sm border-t border-sage-200 px-4 py-3 safe-area-pb" data-testid="floating-cta">
        <Button onClick={() => scrollTo('pricing-section')} className="w-full bg-declic hover:bg-declic/90 text-white h-12 rounded-xl text-base font-semibold shadow-lg">
          Découvrir Déclic RePas — 39 €
        </Button>
      </div>
    </div>
  );
};

export default Landing;
